import { mutationFor, queryFor } from "@visioni/a3s-client-api/app";
import {
  listReportsEndpoint,
  getReportEndpoint,
  getReportByIdEndpoint,
  getReportSnapshotUrlEndpoint,
  getReportArtifactUrlEndpoint,
  getRoadsenseLivePayloadEndpoint,
  getScopedRoadsenseLivePayloadEndpoint,
  getReportEmailDeliveryEndpoint,
  putReportEmailDeliveryEndpoint,
  deleteReportEmailDeliveryEndpoint,
} from "../client-messages";

/**
 * React Query options factories for the reports endpoints.
 *
 * Each factory derives its queryKey, URL builder, and decode boundary from a
 * single `defineEndpoint` definition, so the contract surface (path,
 * pathParams, querySchema, responseSchema) lives in `client-messages` exactly
 * once. Pattern mirrored from `features/a3s-upsell/src/app/queries.ts`.
 *
 * `apiConfig.tenant` carries the tenantId — callers pass only the extra path
 * params.
 */

export const reportsListQuery = queryFor(listReportsEndpoint);
export const reportItemByIdQuery = queryFor(getReportByIdEndpoint);
export const reportSnapshotUrlQuery = queryFor(getReportSnapshotUrlEndpoint);

/**
 * Presigns one per-site artifact (render `.json` OR download `.xlsx`) for a
 * multi-file report. The `key` query param is the opaque S3 key the listing row
 * carries for the site; the server validates + structurally guards it. Used by
 * RoadSense to fetch the render payload and to build the workbook download link.
 */
export const reportArtifactUrlQuery = queryFor(getReportArtifactUrlEndpoint);

/**
 * One RoadSense site's render payload, computed live (see
 * `get-roadsense-live-payload.ts`). Keyed per `(reportId, siteId)`.
 */
export const roadsenseLivePayloadQuery = queryFor(getRoadsenseLivePayloadEndpoint);

/**
 * One RoadSense site's live payload, built from only the movements the caller
 * can view (see `get-scoped-roadsense-live-payload.ts`). Keyed per
 * `(reportId, siteId)`, apart from {@link roadsenseLivePayloadQuery}.
 */
export const scopedRoadsenseLivePayloadQuery = queryFor(getScopedRoadsenseLivePayloadEndpoint);

/** WasteSense-legacy; new types use {@link reportItemByIdQuery}. */
export const reportItemQuery = queryFor(getReportEndpoint);

/** The caller's own report email delivery for one site: off, or on at a frequency. */
export const reportEmailDeliveryQuery = queryFor(getReportEmailDeliveryEndpoint);

/** Starts or changes the caller's report email delivery for one site; answers like the GET. */
export const putReportEmailDeliveryMutation = mutationFor(putReportEmailDeliveryEndpoint);

/** Stops the caller's report email delivery for one site (`204`, whether or not one existed). */
export const deleteReportEmailDeliveryMutation = mutationFor(deleteReportEmailDeliveryEndpoint);
